import { SignalSlashIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { clearPageNotifications, openNotification } from '@/components/UI/DesktopNotifications';
import { ConnectionStatus } from '@/pages/(guarded)/release-center/components/ConnectionStatus';
import {
  dispatchOutdatedAlert,
  dispatchRemoveAlert,
  dispatchShowAlert,
} from '@/renderer/utils/alerts';
import { useInterval } from '@/renderer/utils/hooks/useInterval';
import { useIsFirstRender } from '@/renderer/utils/hooks/useIsFirstRender';
import { useNetworkState } from '@/renderer/utils/hooks/useNetworkState';
import { useRefUpdating } from '@/renderer/utils/hooks/useRefUpdating';
import { useSsr } from '@/renderer/utils/hooks/useSSr';
import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';
import play from '@/renderer/utils/play';
import { IS_SERVER } from '@/renderer/utils/ssr';
import { useIdleContext } from '@/renderer/utils/useIdle';
import useMe, { authToken, refreshToken, useAuthToken } from '@/renderer/utils/useMe';
import useOnlineStatus from '@/renderer/utils/useOnlineStatus';
import { usePageContext } from '@/renderer/utils/usePageContext';
import { useRasContext } from '@/renderer/utils/useRasContext';
import { useRequestFetcher } from '@/renderer/utils/useRequest';
import { useTimeout } from '@/renderer/utils/useTimeout';
import useWebSocket, { WS_BASE_URL } from '@/renderer/utils/useWebSocket2';
import { setWsActivity } from '@/renderer/utils/ws/connection';
import type { Connection } from '@/renderer/utils/ws/types';
import type { RequestSchema } from '@/types/request';

interface ConnectData {
  message: 'AUTHORIZED' | 'UNAUTHORIZED';
  versions: {
    api: string;
    apiDate: number;
    base: string;
    baseDate: number;
    client: string;
    clientDate: number;
    app: string;
    appDate: number;
  };
}

enum WarningLevel {
  None,
  Short,
  Long,
}

const JITTER = 1000 * Math.random();
const WAIT_LONG = 120000; // increased from 30s because we have a separate ConnectionStatusIcon now
const WAIT_SHORT = 2500;
const WAIT_START = 8000;

const APP_DATE = parseInt(`${import.meta.env.VITE_PUBLIC_ENV__APP_DATE}`, 10) || 0;

// Use a random timer 0-250ms to avoid hammering the server all at once
const FLAG_TIMEOUT = Math.floor(Math.random() * 250);

export enum FLAG_STATE {
  // received (WS or polled)
  ACK = 4,
  // sent desktop notification
  OPENED = 5,
  // was shown alert
  READ = 6,
  // was shown message on Release Center / DMs + was not idle
  VISITED = 7,
}

interface EventItem<T extends API.DbItem, Name extends string> {
  event: Name;
  data: T;
}

interface EventFlag<T extends API.DbItem, Name extends string> {
  event: Name;
  data: {
    id?: API.Id;
    item?: T;
    flag: FLAG_STATE;
    idleFlag?: FLAG_STATE;
  };
}

export interface CustomEventMap<T extends API.DbItem = any, Name extends string = any> {
  'ras:webSocketWatcher/created': CustomEvent<EventItem<T, Name>>;
  'ras:webSocketWatcher/flag': CustomEvent<EventFlag<T, Name>>;
}

interface WebSocketWatcherEventTarget extends EventTarget {
  addEventListener<K extends keyof CustomEventMap>(
    type: K,
    listener:
      | ((ev: CustomEventMap[K]) => void)
      | Record<'handleEvent', (evt: CustomEventMap[K]) => void>
      | null,
    options?: boolean | AddEventListenerOptions,
  ): void;
  dispatchEvent<K extends keyof CustomEventMap>(ev: CustomEventMap[K]): boolean;
  removeEventListener<K extends keyof CustomEventMap>(
    type: K,
    listener:
      | ((ev: CustomEventMap[K]) => void)
      | Record<'handleEvent', (evt: CustomEventMap[K]) => void>
      | null,
    options?: boolean | EventListenerOptions,
  ): void;
}

export const webSocketWatcherEventTarget: WebSocketWatcherEventTarget = new EventTarget();

const flagCacheMap = new Map<string, { state: FLAG_STATE; timer?: NodeJS.Timeout }>();
const dmFlagCacheMap = new Map<string, { state: FLAG_STATE; timer?: NodeJS.Timeout }>();

export default function WebSocketWatcher() {
  const me = useMe();
  const meId = useRefUpdating(me?.id);
  const { urlPathname } = usePageContext();
  const pingTime = useRef(-Infinity);
  const pongTime = useRef(Infinity);
  const currentAuthToken = useAuthToken();
  const idle = useIdleContext();
  const inactiveRef = useRefUpdating(idle.inactive);
  const isOnline = useOnlineStatus();
  const knownIds = useRef<Set<API.Id>>(new Set());

  if (!IS_SERVER) {
    // iOS interval to make sure we keep sending silent notifications in an attempt to prevent background WS loss

    const platform = `${
      typeof navigator !== 'undefined' && (navigator.userAgentData?.platform || navigator.platform)
    }`;
    const isIos = platform.startsWith('i');

    // biome-ignore lint/correctness/useHookAtTopLevel: No need for SSR
    useInterval(
      () => {
        isIos &&
          openNotification(
            'RAS is running',
            'Keep this open for real-time notificaitons',
            '/release-center',
            'rasrtn',
            true,
          );
      },
      20 * 60 * 1000, // re-notify silently every 20 minutes
    );

    // biome-ignore lint/correctness/useHookAtTopLevel: No need for SSR
    // biome-ignore lint/correctness/useExhaustiveDependencies(isIos): Does not change
    useEffect(() => {
      isIos &&
        openNotification(
          'RAS is running',
          'Keep this open for real-time notificaitons',
          '/release-center',
          'rasrtn',
          true,
        );
    }, []);
  }

  const isStaff = me?.role === 'staff' || me?.role === 'admin';
  const isStaffRef = useRefUpdating(isStaff);

  // biome-ignore lint/correctness/useExhaustiveDependencies(urlPathname): only used on server-side
  const processMessageReceipt = useCallback((receipt: API.MessageReceipt) => {
    if (knownIds.current.has(receipt.id)) {
      return;
    }

    let stateToSet = FLAG_STATE.ACK;

    const isFromStaff =
      receipt.message?.user?.role === 'staff' || receipt.message?.user?.role === 'admin';
    const isGoMsg = isFromStaff && receipt.message.content?.match(/^\W*GO\W*[.!]*$/i);
    const isCancelMsg = isFromStaff && receipt.message.content?.match(/^\W*CANCEL\W*[.!]*$/i);
    const isOppositeMsg = isFromStaff && receipt.message.content?.match(/^\W*OPPOSITE\W*[.!]*$/i);
    const isSetUpMsg = isFromStaff && receipt.message.content?.match(/^\W*Set up\W*(.*?)*$/i);

    // We don't use urlPathname so it prevents this callback from being re-bound on navigation
    const pathname = IS_SERVER ? urlPathname : location.pathname;
    const notOnPage = !pathname.startsWith('/release-center');

    if (inactiveRef.current) {
      if (openNotification('RAS Release Center', receipt.message.content, '/release-center')) {
        stateToSet = FLAG_STATE.OPENED;
      }
    }

    // Not on releases page, but received a play message
    if (notOnPage) {
      stateToSet = FLAG_STATE.READ;

      dispatchShowAlert({
        id: `msg_${receipt.id}`,
        status: 'info',
        message: receipt.message.content,
        submessage: receipt.message.release?.playText,
        href: '/release-center',
      });
    }

    if (receipt.createdAt) {
      const now = Date.now();
      const then = Date.parse(receipt.createdAt);

      if (then && now - then > 30000) {
        // Only play sounds within 30s. If they come late, we don't want users to hear the wrong GO message.
        return;
      }
    }

    knownIds.current.add(receipt.id);

    // See if we need to flag the message as received by the client
    if (receipt.web < stateToSet) {
      webSocketWatcherEventTarget.dispatchEvent(
        new CustomEvent('ras:webSocketWatcher/flag', {
          detail: {
            event: `messageReceipt/flag`,
            data: {
              id: receipt.id,
              item: receipt,
              flag: stateToSet,
            },
          },
        }),
      );
    }

    if (knownIds.current.size > 20) {
      // Purge items to keep this list small
      for (let i = 10; i--; ) {
        const [first] = knownIds.current;
        knownIds.current.delete(first);
      }
    }

    if (isGoMsg) {
      play('https://cdn.raspicks.com/go-25.mp3');
    } else if (isCancelMsg) {
      play('https://cdn.raspicks.com/cancel-25.mp3');
    } else if (isOppositeMsg) {
      play('https://cdn.raspicks.com/opposite-25.mp3');
    } else if (isSetUpMsg) {
      play('https://cdn.raspicks.com/setup-25.mp3');
    } else {
      play('https://cdn.raspicks.com/msg.mp3');
    }
  }, []);

  const createdCallback = useCallback(
    (
      event: CustomEvent<
        | EventItem<API.MessageReceipt, 'messageReceipt/created'>
        | EventItem<API.DirectMessageReceipt, 'directMessageReceipt/created'>
      >,
    ) => {
      const eventName = event.detail.event;

      if (eventName === 'messageReceipt/created') {
        processMessageReceipt(event.detail.data);
      } else if (eventName === 'directMessageReceipt/created') {
        if (inactiveRef.current) {
          if (event.detail.data.userId === meId.current) {
            // dont show if i sent this message
          }
          if (
            isStaffRef.current &&
            (!event.detail.data.userId || event.detail.data.receiverUserId)
          ) {
            // dont show if im an admin and this is a message from an admin
          }
          if (
            openNotification(
              'RAS Support',
              event.detail.data.content,
              isStaffRef.current && event.detail.data.userId
                ? `/support-chat/${event.detail.data.userId}`
                : '/support-chat',
            )
          ) {
            // @todo use processMessageReceipt so we can send message acks
            // stateToSet = FLAG_STATE.OPENED;
          }
        }
      }
    },
    [processMessageReceipt],
  );

  const messageReceiptFlagRequest: RequestSchema<
    API.DirectMessageReceipt,
    API.WithData<{ type: 'message' | 'dm'; web: number }>
  > = useMemo(
    () => ({
      name: 'messageReceiptFlagRequest',
      input: `/api/v1/messageReceipt/me/:id/flag`,
      method: 'POST',
    }),
    [],
  );

  const updateMessageRequest = useRequestFetcher(messageReceiptFlagRequest, { manual: true });

  const flagCallback = useCallback(
    (
      event: CustomEvent<
        | EventFlag<API.MessageReceipt, 'messageReceipt/flag'>
        | EventFlag<API.DirectMessageReceipt, 'directMessageReceipt/flag'>
      >,
    ) => {
      const eventName = event.detail.event;
      const itemId = event.detail.data.id || event.detail.data.item?.id;
      const idleFlag = event.detail.data.idleFlag;
      const nextFlag = idleFlag && inactiveRef.current ? idleFlag : event.detail.data.flag;
      const cache = eventName === 'messageReceipt/flag' ? flagCacheMap : dmFlagCacheMap;
      const type = eventName === 'messageReceipt/flag' ? 'message' : 'dm';
      const id = `${itemId}`;

      if (itemId) {
        const prev = cache.get(id);
        const cb = () =>
          updateMessageRequest.run(
            { data: { type, web: nextFlag } },
            {
              req: { input: messageReceiptFlagRequest.input.toString().replace(':id', id) },
              dontAlterState: true,
              dontStoreError: true,
            },
          );

        if (prev) {
          // If we already had a flag pending for this one, queue up a new one if the new flag is higher
          if (prev.state < nextFlag) {
            clearTimeout(prev.timer);

            cache.set(id, {
              state: nextFlag,
              timer: setTimeout(cb, 500 + JITTER),
            });
          }
        } else {
          cache.set(id, {
            state: nextFlag,
            timer: setTimeout(cb, 500 + JITTER),
          });
        }

        // Clean up
        setTimeout(
          () => {
            if (cache.size > 30) {
              for (const [key] of cache) {
                cache.delete(key);

                if (cache.size <= 30) {
                  break;
                }
              }
            }
          },
          (500 + JITTER) * 4,
        );
      }
    },
    [],
  );

  useEffect(() => {
    // Only happens client-side
    webSocketWatcherEventTarget.addEventListener(
      'ras:webSocketWatcher/created',
      createdCallback,
      false,
    );
    webSocketWatcherEventTarget.addEventListener('ras:webSocketWatcher/flag', flagCallback, false);

    return () => {
      webSocketWatcherEventTarget.removeEventListener(
        'ras:webSocketWatcher/created',
        createdCallback,
        false,
      );
      webSocketWatcherEventTarget.removeEventListener(
        'ras:webSocketWatcher/flag',
        flagCallback,
        false,
      );
    };
  }, [createdCallback, flagCallback]);

  const onWatcherCreated = useCallback(
    (_event: MessageEvent, response: API.SubscriptionResponse<API.MessageReceipt>) =>
      processMessageReceipt(response.data),
    [processMessageReceipt],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  const onWatcherDmCreated = useCallback(
    (_event: MessageEvent, json: API.SubscriptionResponse<API.DirectMessageReceipt>) => {
      // We don't use urlPathname so it prevents this callback from being re-bound on navigation
      const pathname = IS_SERVER ? urlPathname : location.pathname;
      let dmPath = '/support-chat';
      let submessage = json.data.user?.firstName || 'User';
      let chatId: string | number = 'me';

      if (isStaffRef.current) {
        if (!json.data.receiverUserId) {
          chatId = json.data.userId; // msg from user
          submessage = `${submessage} ←`;
        } else if (json.data.receiverUserId) {
          chatId = json.data.receiverUserId; // msg from admin
          submessage = `${submessage} (RAS) → ${
            json.data.receiverUser?.firstName || json.data.receiverUserId
          }`;
        }

        dmPath = `${dmPath}/${chatId}`;

        if (json.data.receiverUserId) {
          // When msg from admin to user, clear the existing notification if any (we don't need to attend to it anymore)
          clearPageNotifications(dmPath);
          dispatchRemoveAlert(`dm_${chatId}`);

          return;
        }
      }

      // Not on live chat, but received a DM
      if (pathname !== dmPath) {
        dispatchShowAlert({
          id: `dm_${chatId}`,
          status: 'info',
          message: json.data.content,
          submessage,
          href: dmPath,
          uId: json.data.userId,
          uFirst: json.data.user?.firstName,
          uLast: json.data.user?.lastName,
          uRole: json.data.user?.role,
        });
      }

      if (inactiveRef.current) {
        openNotification('RAS Support', json.data.content, dmPath);
      }

      // 1647 is Cleatus
      if (dmPath === '/support-chat/1647') {
        return;
      }

      if ((!isStaffRef.current && json.data.receiverUserId) || pathname !== dmPath) {
        // Regular users always get DM sound receipts, admins only when not on that DM page
        play('https://cdn.raspicks.com/pop.mp3');
      }
    },
    [],
  );

  const onWatcherClose = useCallback((event: Event) => {
    console.log('she closed', event);

    // @see PageTokenRefresher: the event.reason === 401, 402, 403 check which sets authToken(null) is not here
  }, []);

  const onWatcherReady = useCallback((event: MessageEvent) => {
    const data: ConnectData = JSON.parse(event.data);

    // The native (Capacitor) app's bundle is baked in and only updates through
    // the App Store, so "you're out of date — reload" can't fix a mismatch on
    // native; it just loops (the server deploys independently of app releases).
    // Only prompt on the web, where a reload actually fetches the new bundle.
    //
    // Caveat: this compares the server's build-time appDate against the client
    // bundle's baked APP_DATE, so a server-only (or manual) deploy that stamps a
    // newer appDate than the currently-deployed web bundle will prompt reload on
    // every reconnect until the viteclient is redeployed to match. Keep server
    // and viteclient deploys in lockstep (same run) so the two appDates agree.
    const isNative = !!(
      window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }
    ).Capacitor?.isNativePlatform?.();

    if (!isNative && data?.versions?.appDate && APP_DATE && data.versions.appDate > APP_DATE) {
      dispatchOutdatedAlert();
    }
  }, []);

  const onWatcherMessage = useCallback(function (
    this: Connection,
    event: MessageEvent,
    _subscriber:
      | API.SubscriptionResponse<API.MessageReceipt | API.DirectMessageReceipt, string>
      | null
      | undefined,
  ) {
    if (event.data === 'PONG') {
      pongTime.current = Date.now();
      const ping = pongTime.current - pingTime.current;

      if (ping > 500) {
        console.log('High ping:', ping);
      }
    } else if (event.data.startsWith('{"message":"UNAUTHORIZED"')) {
      // not allowed; clear queue and disconnect
      authToken(null);
    } else if (event.data.startsWith('{"message":"EXPIRED"')) {
      // refresh token and reconnect
      this.close();
      console.log('gonna attempt to recon from expired');
      refreshToken().then(() => this.reconnect());
    }
  }, []);

  const onWatcherAuthLogout = useCallback(
    (_event: MessageEvent, json: API.SubscriptionResponse<{ code: string; message: string }>) => {
      dispatchShowAlert({
        id: `auth_logout`,
        status: 'info',
        message:
          (json.data.code === 'AUTH/LOGOUT:SESSION_LIMIT'
            ? 'You have logged in from too many devices, and have been logged out from this one.'
            : json.data.message) || 'You have been logged out from another location.',
        // submessage: receipt.message.release?.playText,
        href: '/signin',
        // permanent: true,
        duration: 60 * 60 * 24 * 7 * 1000, // this is better than permanent, as it is dismissable
      });
    },
    [],
  );

  useUpdateEffect(() => {
    if (me?.id) {
      dispatchRemoveAlert('auth_logout');
    }
  }, [me?.id]);

  const { ready } = useWebSocket(WS_BASE_URL, {
    onClose: onWatcherClose,
    onMessage: onWatcherMessage,
    onReady: onWatcherReady,
    subscriptions: {
      'messageReceipt/created': onWatcherCreated,
      'directMessageReceipt/created': onWatcherDmCreated,
      'auth/logout': onWatcherAuthLogout,
    },
    connect: !!currentAuthToken,
  });

  // Tell the server whether the user is actively watching (foreground + focused
  // + not idle) so it can suppress a redundant DM push while they're present and
  // let it through once they minimize/background. Re-sent whenever readiness or
  // activity changes — the readiness dependency covers reconnects, which the
  // server starts as active until it hears otherwise.
  useEffect(() => {
    if (ready) {
      setWsActivity(!idle.inactive);
    }
  }, [ready, idle.inactive]);

  const [showWarning, setShowWarning] = useState(WarningLevel.None);

  const isFirstRender = useIsFirstRender();
  const { isBrowser } = useSsr();

  // If we haven't been able to connect to the WS recently, show the delayed message
  const timeoutShort = useTimeout(
    () => {
      if (isBrowser) {
        setShowWarning(WarningLevel.Short);
      }
    },
    !ready && currentAuthToken ? (isFirstRender ? WAIT_START : WAIT_SHORT) : null,
  );

  // If we haven't been able to connect to the WS for over (WAIT_LONG), prompt them to refresh.
  // Paused while the user is inactive (tab hidden, no focus, idle) so the timer only counts
  // down against time the user is actively watching the page. The delay dependency resets the
  // clock whenever inactive→active transitions happen, giving a clean 2-minute window each time.
  const timeoutLong = useTimeout(
    () => {
      if (isBrowser) {
        setShowWarning(WarningLevel.Long);
      }
    },
    !ready && currentAuthToken && !idle.inactive ? WAIT_LONG : null,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useEffect(() => {
    if (ready) {
      clearInterval(timeoutShort.current as number);
      clearInterval(timeoutLong.current as number);
      setShowWarning(WarningLevel.None);
    }
  }, [ready]);

  const rasContext = useRasContext();

  // RasContext ConnectionStatus handler begins
  const networkState = useNetworkState();

  const baseConnectionStatus = useMemo(() => {
    if (!IS_SERVER) {
      if (networkState.online) {
        if (networkState.effectiveType === '2g' || networkState.effectiveType === 'slow-2g') {
          return ConnectionStatus.SlowCellular;
        } else {
          return ConnectionStatus.Stable;
        }
      } else {
        return ConnectionStatus.Offline;
      }
    }
    return ConnectionStatus.Loading;
  }, [networkState.effectiveType, networkState.online]);

  const recentDisconnectionCountRef = useRef(0);
  const lastCountCheckRef = useRef(0);

  useEffect(() => {
    if (showWarning === WarningLevel.Short) {
      recentDisconnectionCountRef.current++;
    }
  }, [showWarning]);

  useEffect(() => {
    if (baseConnectionStatus === ConnectionStatus.Offline) {
      // Offline overrides all else
      rasContext.setData?.('connectionStatus', baseConnectionStatus);
    } else if (showWarning === WarningLevel.Long) {
      // If we lost WS has been long disconnected, consider WS unstable
      rasContext.setData?.('connectionStatus', ConnectionStatus.WebsocketDisconnected);
    } else if (showWarning === WarningLevel.Short) {
      // WS currently disconnected, possibly temporarily
      rasContext.setData?.('connectionStatus', ConnectionStatus.WebsocketDisconnected);
    } else if (recentDisconnectionCountRef.current > 2) {
      // If we lost WS connection twice in the last 60s, consider WS unstable
      rasContext.setData?.('connectionStatus', ConnectionStatus.WebsocketUnstable);
    } else {
      rasContext.setData?.('connectionStatus', baseConnectionStatus);
    }
    // @todo handle ConnectionStatus.PollingUnstable
  }, [showWarning, rasContext.setData, baseConnectionStatus]);

  useInterval(() => {
    // Only reset count if it hasn't changed in 60s
    if (lastCountCheckRef.current === recentDisconnectionCountRef.current) {
      recentDisconnectionCountRef.current = 0;
    }

    lastCountCheckRef.current = recentDisconnectionCountRef.current;
  }, 60000);
  // RasContext ConnectionStatus handler ends

  if (
    currentAuthToken &&
    showWarning === WarningLevel.Long /* || showWarning === WarningLevel.Short*/
  ) {
    return (
      <>
        <aside className="sticky top-0 bg-amber-50 p-4 dark:bg-amber-500/10">
          <div className="flex flex-row items-start space-x-2 text-sm text-amber-700 dark:text-amber-300">
            <SignalSlashIcon height={24} className="size-6 shrink-0" />
            <div className="flex flex-col space-x-2">
              {showWarning === WarningLevel.Long
                ? `You've been experiencing a degraded connection for quite some time. ${
                    isOnline
                      ? 'Something might be wrong with the app.'
                      : 'Your internet connection appears to be offline.'
                  } `
                : `Degraded connection. Messaging service may be delayed.`}
              {showWarning === WarningLevel.Long && (
                <a
                  href="#"
                  className="mt-3 inline-block w-fit rounded px-2.5 py-1.5 text-amber-700 ring-1 ring-amber-700 hover:bg-amber-700/5 hover:text-amber-800 dark:text-amber-300 dark:ring-amber-300 dark:hover:bg-amber-300/5 dark:hover:text-amber-400"
                  onClick={(event) => {
                    event.preventDefault();
                    location.reload();
                  }}
                >
                  Refresh
                </a>
              )}
            </div>
          </div>
        </aside>
      </>
    );
  }

  return null;
}
